import {
  Button,
  Card,
  FileButton,
  Group,
  Loader,
  Stack,
  TextInput,
  Title,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconUpload } from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/lib/api/client";
import { getSettings, updateSettings, uploadLogo } from "@/lib/api/settings";
import { useAuthStore } from "@/stores/auth";

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const canEdit = useAuthStore((s) => s.hasPermission("settings.update"));
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: getSettings,
  });

  const form = useForm({
    initialValues: {
      name: "",
      legal_name: "",
      inn: "",
      license_number: "",
      address: "",
      phone: "",
    },
  });

  useEffect(() => {
    if (data?.clinic) {
      form.setValues({
        name: data.clinic.name ?? "",
        legal_name: data.clinic.legal_name ?? "",
        inn: data.clinic.inn ?? "",
        license_number: data.clinic.license_number ?? "",
        address: data.clinic.address ?? "",
        phone: data.clinic.phone ?? "",
      });
    }
  }, [data?.clinic.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      notifications.show({ color: "green", message: t("settings.saved") });
    },
  });

  const logoMutation = useMutation({
    mutationFn: uploadLogo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      notifications.show({ color: "green", message: t("settings.logo_uploaded") });
    },
  });

  const previewMutation = useMutation({
    mutationFn: async (tpl: string) => {
      const response = await api.get<Blob>(`/settings/template-preview/${tpl}`, {
        params: { lang: i18n.language },
        responseType: "blob",
      });
      return response.data as Blob;
    },
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    },
    onError: () => {
      notifications.show({ color: "red", message: t("settings.preview_failed") });
    },
  });

  if (isLoading) return <Loader />;

  return (
    <Stack gap="md">
      <Title order={2}>{t("settings.title")}</Title>

      <Card withBorder padding="md">
        <Title order={4} mb="sm">
          {t("settings.clinic_info")}
        </Title>
        <form
          onSubmit={form.onSubmit((vals) =>
            saveMutation.mutate({
              name: vals.name,
              legal_name: vals.legal_name || null,
              inn: vals.inn || null,
              license_number: vals.license_number || null,
              address: vals.address || null,
              phone: vals.phone || null,
            }),
          )}
        >
          <Stack>
            <TextInput
              label={t("settings.fields.name")}
              placeholder={t("placeholder.name")}
              required
              disabled={!canEdit}
              {...form.getInputProps("name")}
            />
            <TextInput
              label={t("settings.fields.legal_name")}
              placeholder={t("placeholder.legal_name")}
              disabled={!canEdit}
              {...form.getInputProps("legal_name")}
            />
            <Group grow>
              <TextInput
                label={t("settings.fields.inn")}
                placeholder={t("placeholder.inn")}
                disabled={!canEdit}
                {...form.getInputProps("inn")}
              />
              <TextInput
                label={t("settings.fields.license")}
                placeholder={t("placeholder.license")}
                disabled={!canEdit}
                {...form.getInputProps("license_number")}
              />
            </Group>
            <TextInput
              label={t("settings.fields.address")}
              placeholder={t("placeholder.address")}
              disabled={!canEdit}
              {...form.getInputProps("address")}
            />
            <TextInput
              label={t("settings.fields.phone")}
              placeholder={t("placeholder.phone")}
              disabled={!canEdit}
              {...form.getInputProps("phone")}
            />
            {canEdit && (
              <Group justify="flex-end">
                <Button type="submit" loading={saveMutation.isPending}>
                  {t("common.save")}
                </Button>
              </Group>
            )}
          </Stack>
        </form>
      </Card>

      <Card withBorder padding="md">
        <Title order={4} mb="sm">
          {t("settings.logo")}
        </Title>
        <Stack>
          <Text c="dimmed" size="sm">
            {t("settings.logo_hint")}
          </Text>
          {canEdit && (
            <Group>
              <FileButton
                onChange={(file) => file && logoMutation.mutate(file)}
                accept="image/png,image/jpeg,image/webp"
              >
                {(props) => (
                  <Button
                    {...props}
                    leftSection={<IconUpload size={16} />}
                    loading={logoMutation.isPending}
                  >
                    {t("settings.upload_logo")}
                  </Button>
                )}
              </FileButton>
              {data?.clinic.logo_object_key && (
                <Text size="sm" c="dimmed">
                  {t("settings.logo_attached")}
                </Text>
              )}
            </Group>
          )}
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Title order={4} mb="sm">
          {t("settings.templates")}
        </Title>
        <Stack gap="sm">
          <Text c="dimmed" size="sm">
            {t("settings.templates_hint")}
          </Text>
          <Group>
            {(["form_086", "form_083", "form_089"] as const).map((tpl) => (
              <Button
                key={tpl}
                variant="light"
                onClick={() => previewMutation.mutate(tpl)}
                loading={
                  previewMutation.isPending && previewMutation.variables === tpl
                }
              >
                {t(`examinations.types.${tpl}`)} — {t("settings.preview")}
              </Button>
            ))}
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
